import React, { useState, useEffect } from 'react';
import { 
  Card, 
  DatePicker, 
  Button, 
  Space, 
  Row, 
  Col, 
  Typography,
  message,
  Tag,
  Divider,
  Alert
} from 'antd';
import { 
  ShopOutlined,
  CalendarOutlined,
  SaveOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined
} from '@ant-design/icons';
import './ControleLoja.css';
import fundoNovaGeracao from '../images/FUNDO_NOVAGERACAO_SEMPRE.png';
import { buildApiUrl } from '../utils/api';
import dayjs from 'dayjs';
import isBetween from 'dayjs/plugin/isBetween';

dayjs.extend(isBetween);

const { Title, Paragraph, Text } = Typography;
const { RangePicker } = DatePicker;

const ControleLoja = () => {
  const [periodo, setPeriodo] = useState(null);
  const [periodoAtivo, setPeriodoAtivo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [lojaAtiva, setLojaAtiva] = useState(false);

  useEffect(() => {
    fetchPeriodoLoja();
  }, []);

  const fetchPeriodoLoja = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(buildApiUrl('/api/loja/periodo'), {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.periodo) {
          setPeriodoAtivo({
            dataInicio: dayjs(data.periodo.data_inicio),
            dataFim: dayjs(data.periodo.data_fim)
          });
          
          // Verificar se a loja está ativa agora
          const agora = dayjs();
          const estaAtiva = agora.isBetween(
            dayjs(data.periodo.data_inicio), 
            dayjs(data.periodo.data_fim), 
            null, 
            '[]'
          );
          setLojaAtiva(estaAtiva);
        }
      }
    } catch (error) {
      console.error('Erro ao buscar período da loja:', error);
    }
  };

  const handleSalvar = async () => {
    if (!periodo || periodo.length !== 2) {
      message.error('Por favor, selecione o período completo (data inicial e final)');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(buildApiUrl('/api/loja/periodo'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          data_inicio: periodo[0].format('YYYY-MM-DD') + ' 00:00:00',
          data_fim: periodo[1].format('YYYY-MM-DD') + ' 23:59:59'
        })
      });

      if (response.ok) {
        message.success('Período da loja salvo com sucesso!');
        setPeriodo(null);
        fetchPeriodoLoja();
      } else {
        const data = await response.json();
        message.error(data.message || 'Erro ao salvar período da loja');
      }
    } catch (error) {
      console.error('Erro ao salvar período:', error);
      message.error('Erro ao salvar período da loja');
    } finally {
      setLoading(false);
    }
  };

  const handleDesativar = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(buildApiUrl('/api/loja/periodo'), {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        message.success('Período da loja removido com sucesso!');
        setPeriodoAtivo(null);
        setLojaAtiva(false);
      } else {
        const data = await response.json();
        message.error(data.message || 'Erro ao remover período da loja');
      }
    } catch (error) {
      console.error('Erro ao remover período:', error);
      message.error('Erro ao remover período da loja');
    } finally {
      setLoading(false);
    }
  };

  const disabledDate = (current) => {
    // Não permitir datas anteriores a hoje (permite hoje)
    return current && current < dayjs().subtract(1, 'day').endOf('day');
  };

  return (
    <Space direction="vertical" size={24} style={{ width: '100%', padding: '24px', background: '#fff', minHeight: '100vh' }}>
      {/* Header com Background */}
      <Card
        bordered={false}
        bodyStyle={{
          padding: 0,
          background: `linear-gradient(135deg, rgba(62, 121, 247, 0.95) 0%, rgba(30, 90, 220, 0.95) 100%)`,
          backgroundImage: `url(${fundoNovaGeracao})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundBlendMode: 'overlay',
          borderRadius: '8px',
          minHeight: '140px',
          display: 'flex',
          alignItems: 'center',
        }}
      >
        <Row justify="center" align="middle" style={{ width: '100%', padding: '40px 40px' }}>
          <Col style={{ textAlign: 'center' }}>
            <Space direction="vertical" size={12}>
              <Title level={1} style={{ color: '#fff', margin: 0, fontWeight: 700, fontSize: '42px' }}>
                <ShopOutlined /> Controle da Loja
              </Title>
              <Paragraph style={{ color: 'rgba(255, 255, 255, 0.95)', margin: 0, fontSize: '20px', fontWeight: 400 }}>
                Configure o período de funcionamento da loja
              </Paragraph>
            </Space>
          </Col>
        </Row>
      </Card>

      {/* Status Atual */}
      <Card title={<><CheckCircleOutlined /> Status da Loja</>}>
        <Row gutter={[24, 24]}>
          <Col xs={24} md={12}>
            <div style={{ textAlign: 'center', padding: '24px' }}>
              <div style={{ fontSize: '48px', marginBottom: '16px' }}>
                {lojaAtiva ? (
                  <CheckCircleOutlined style={{ color: '#52c41a' }} />
                ) : (
                  <CloseCircleOutlined style={{ color: '#ff4d4f' }} />
                )}
              </div>
              <Title level={3} style={{ margin: 0 }}>
                {lojaAtiva ? 'Loja Ativa' : 'Loja Desativada'}
              </Title>
              <Paragraph style={{ color: '#666', marginTop: '8px' }}>
                {lojaAtiva 
                  ? 'Os clientes podem fazer pedidos normalmente'
                  : 'Os clientes não podem fazer pedidos no momento'
                }
              </Paragraph>
            </div>
          </Col>
          <Col xs={24} md={12}>
            {periodoAtivo ? (
              <div style={{ padding: '24px' }}>
                <Space direction="vertical" size={16} style={{ width: '100%' }}>
                  <div>
                    <Text strong style={{ fontSize: '16px' }}>Período Ativo:</Text>
                  </div>
                  <div>
                    <CalendarOutlined style={{ marginRight: '8px', color: '#1890ff' }} />
                    <Text strong>Data Início:</Text>
                    <div style={{ marginLeft: '24px', marginTop: '4px' }}>
                      <Tag color="blue" style={{ fontSize: '14px', padding: '4px 12px' }}>
                        {periodoAtivo.dataInicio.format('DD/MM/YYYY')}
                      </Tag>
                    </div>
                  </div>
                  <div>
                    <CalendarOutlined style={{ marginRight: '8px', color: '#1890ff' }} />
                    <Text strong>Data Fim:</Text>
                    <div style={{ marginLeft: '24px', marginTop: '4px' }}>
                      <Tag color="blue" style={{ fontSize: '14px', padding: '4px 12px' }}>
                        {periodoAtivo.dataFim.format('DD/MM/YYYY')}
                      </Tag>
                    </div>
                  </div>
                  <Divider style={{ margin: '8px 0' }} />
                  <Button 
                    type="primary" 
                    danger 
                    onClick={handleDesativar}
                    loading={loading}
                    icon={<CloseCircleOutlined />}
                  >
                    Desativar Loja
                  </Button>
                </Space>
              </div>
            ) : (
              <Alert
                message="Nenhum período configurado"
                description="Configure um período abaixo para ativar a loja"
                type="warning"
                showIcon
                style={{ margin: '24px' }}
              />
            )}
          </Col>
        </Row>
      </Card>

      {/* Configurar Novo Período */}
      <Card title={<><CalendarOutlined /> Configurar Período de Funcionamento</>}>
        <Space direction="vertical" size={24} style={{ width: '100%' }}>
          <Alert
            message="Importante"
            description="Após salvar o período, a loja ficará ativa apenas durante as datas selecionadas. Fora desse período, os clientes verão a mensagem de loja desativada."
            type="info"
            showIcon
          />

          <Row gutter={[16, 16]} align="bottom">
            <Col xs={24} md={18}>
              <div>
                <label style={{ 
                  display: 'block', 
                  marginBottom: '8px', 
                  fontWeight: 600,
                  fontSize: '14px'
                }}>
                  Selecione o Período:
                </label>
                <RangePicker
                  style={{ width: '100%', height: '40px' }}
                  placeholder={['Data Início', 'Data Fim']}
                  format="DD/MM/YYYY"
                  value={periodo}
                  onChange={(dates) => setPeriodo(dates)}
                  disabledDate={disabledDate}
                />
              </div>
            </Col>
            <Col xs={24} md={6}>
              <Button
                type="primary"
                onClick={handleSalvar}
                loading={loading}
                disabled={!periodo || periodo.length !== 2}
                icon={<SaveOutlined />}
                style={{ width: '100%', height: '40px' }}
              >
                Salvar Período
              </Button>
            </Col>
          </Row>

          {periodo && periodo.length === 2 && (
            <Alert
              message="Período Selecionado"
              description={
                <div>
                  <Text>A loja ficará ativa de </Text>
                  <Text strong>{periodo[0].format('DD/MM/YYYY')}</Text>
                  <Text> até </Text>
                  <Text strong>{periodo[1].format('DD/MM/YYYY')}</Text>
                </div>
              }
              type="success"
              showIcon
            />
          )}
        </Space>
      </Card>

      {/* Informações Adicionais */}
      <Card title="Como funciona?">
        <Space direction="vertical" size={12}>
          <Paragraph>
            <Text strong>1. Configure o período:</Text> Selecione a data e hora de início e fim do período em que a loja ficará ativa.
          </Paragraph>
          <Paragraph>
            <Text strong>2. Salve as alterações:</Text> Clique no botão "Salvar Período" para ativar a loja durante o período selecionado.
          </Paragraph>
          <Paragraph>
            <Text strong>3. Durante o período:</Text> Os clientes poderão acessar a loja e fazer pedidos normalmente.
          </Paragraph>
          <Paragraph>
            <Text strong>4. Fora do período:</Text> A loja ficará automaticamente desativada e os clientes verão a mensagem: "A loja está temporariamente desativada. Em breve voltaremos!"
          </Paragraph>
          <Paragraph>
            <Text strong>5. Desativar manualmente:</Text> Você pode desativar a loja a qualquer momento clicando no botão "Desativar Loja".
          </Paragraph>
        </Space>
      </Card>
    </Space>
  );
};

export default ControleLoja;


